import { Badge } from "@mantine/core";
import {
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
} from "react";
import { type ClassNameValue, twMerge } from "tailwind-merge";

import {
  ScreenerLocationStatus,
  ScreenerLocationStatusType,
} from "@/types/screener-location";
import { CompetencyFit } from "@/types/screeners";
import type { SingleOrArray } from "@/types/utils";
import { type BadgeStyles } from "@/utils/competency";

export const INFO = "Info" as const;
export type AskedCompetencyFit = Exclude<CompetencyFit, CompetencyFit.NotAsked>;
export type UpwageBadgeColors =
  | AskedCompetencyFit
  | ScreenerLocationStatusType
  | typeof INFO;

interface UpwageBadgeProps {
  className?: SingleOrArray<ClassNameValue>;
  colorVariant: UpwageBadgeColors;
  filled?: boolean;
  leftSection?: ReactNode;
}

const FILLED_BADGE_COLORS: Record<UpwageBadgeColors, BadgeStyles> = {
  [CompetencyFit.VeryHigh]: { background: "#B8ECE3", text: "#005648" },
  [CompetencyFit.High]: { background: "#C4E8A9", text: "#1D5200" },
  [CompetencyFit.Medium]: { background: "#FFF4B2", text: "#706000" },
  [CompetencyFit.Low]: { background: "#FFD39F", text: "#6C3B00" },
  [CompetencyFit.VeryLow]: { background: "#FFC9B3", text: "#822716" },
  [CompetencyFit.Absent]: { background: "#DDDDDD", text: "#474747" },
  [INFO]: { background: "#DDDDDD", text: "#474747" },
  [ScreenerLocationStatus.Live]: {
    background: "mint-green.2",
    text: "mint-green.10",
  },
  [ScreenerLocationStatus.Mixed]: {
    background: "#E9F7F5",
    text: "#7CA6A0",
  },
  [ScreenerLocationStatus.Paused]: {
    background: "gray.0",
    text: "gray.5",
  },
};
const OUTLINE_BADGE_COLORS: Record<UpwageBadgeColors, BadgeStyles> = {
  [CompetencyFit.VeryHigh]: { background: "#00806A", text: "#00806A" },
  [CompetencyFit.High]: { background: "#3C8600", text: "#3C8600" },
  [CompetencyFit.Medium]: { background: "#8C7400", text: "#8C7400" },
  [CompetencyFit.Low]: { background: "#B46000", text: "#B46000" },
  [CompetencyFit.VeryLow]: { background: "#E81F00", text: "#E81F00" },
  [CompetencyFit.Absent]: { background: "#666666", text: "#666666" },
  [INFO]: { background: "#666666", text: "#666666" },
  [ScreenerLocationStatus.Live]: {
    background: "mint-green.2",
    text: "mint-green.2",
  },
  [ScreenerLocationStatus.Mixed]: {
    background: "#E9F7F5",
    text: "#E9F7F5",
  },
  [ScreenerLocationStatus.Paused]: {
    background: "gray.0",
    text: "gray.0",
  },
};

export const UpwageBadge = forwardRef(
  (
    {
      children,
      className,
      colorVariant,
      filled,
      leftSection,
    }: PropsWithChildren<UpwageBadgeProps>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const colors = useMemo(
      () => (filled ? FILLED_BADGE_COLORS : OUTLINE_BADGE_COLORS)[colorVariant],
      [colorVariant, filled],
    );
    return (
      <Badge
        c={colors.text}
        className={twMerge(["px-3 py-0.5 text-xs", className])}
        color={colors.background}
        leftSection={leftSection}
        ref={ref}
        size="lg"
        variant={filled ? "filled" : "outline"}
      >
        {children}
      </Badge>
    );
  },
);
